import { create } from 'zustand';
import { persist, subscribeWithSelector, devtools } from 'zustand/middleware';
import socketService from '../services/socketService';

// App-level store to replace React Context
const useAppStore = create(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          // Connection state
          socket: null,
          connectionStatus: 'initializing',
          connectionError: '',
          reconnectionStatus: 'none',
          
          // User state
          username: '',
          currentRoom: '',
          isSignedIn: false,
          
          // Game state
          board: null,
          draw: null,
          messages: [],
          showLoadingScreen: true,
          
          // Actions
          setSocket: (socket) => set({ socket }),
          setConnectionStatus: (status) => set({ connectionStatus: status }),
          setConnectionError: (error) => set({ connectionError: error }),
          
          setUsername: (username, walletAddress = null) => {
            set({ username, isSignedIn: Boolean(username) });
            localStorage.setItem('username', username);
            
            // Get wallet address from parameter or localStorage
            const finalWalletAddress = walletAddress || localStorage.getItem('walletAddress') || null;
            socketService.setUsername(username, finalWalletAddress);
          },
          
          setCurrentRoom: (room) => set({ currentRoom: room }),
          setBoard: (board) => set({ board }),
          setDraw: (draw) => set({ draw }),
          setMessages: (messages) => set({ messages }),
          setShowLoadingScreen: (show) => set({ showLoadingScreen: show }),
          
          // Complex actions
          initializeSocket: async (walletAddress, isBrave) => {
            try {
              const socket = socketService.connect(walletAddress);
              
              // Wait a moment for connection to establish
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              if (socketService.socket?.connected) {
                set({ socket, connectionStatus: 'connected' });
              } else {
                set({ socket, connectionStatus: 'connecting' });
              }
              
              return socket;
            } catch (error) {
              set({ connectionStatus: 'error', connectionError: error.message });
              throw error;
            }
          },
          
          joinGame: async (roomId = 'public', navigate, walletAddress = null) => {
            const { username } = get();
            
            const socketId = socketService.socket?.id;
            
            if (!socketId) {
              return;
            }
            
            // Simple join process - let backend handle everything
            socketService.join(roomId, username);
            
            // Get wallet address from localStorage and pass it
            // const walletAddress = localStorage.getItem('walletAddress') || null;
            socketService.setUsername(username, walletAddress);
            
            // Navigate to waiting room (backend will emit roomAssigned with actual roomId)
            navigate('/game-lobby/waiting');
          },
          
          leaveRoom: (navigate) => {
            console.log('🚪 Leaving room');
            
            socketService.leaveRoom();
            
            navigate('/game-lobby/lobby');
            set({ 
              currentRoom: '', 
              board: null, 
              draw: null, 
              messages: [] 
            });
          },
          
          signOut: (navigate) => {
            console.log('👋 Signing out');
            localStorage.removeItem('username');
            localStorage.removeItem('walletAddress');
            
            const autoUsername = `Player${Math.floor(Math.random() * 1000)}`;
            set({ 
              username: autoUsername, 
              isSignedIn: false 
            });
            socketService.setUsername(autoUsername, null);
            
            navigate('/signin');
          },
          
          // Message handling
          addMessage: (message) => set((state) => ({
            messages: [...state.messages, message]
          })),
          // Add to your actions:
setReconnectionStatus: (status) => set({ reconnectionStatus: status }),

          // Removed: All localStorage user management and reconnect features
          
          // Reset functions
          resetGameState: () => set({
            board: null,
            draw: null,
            messages: [],
            currentRoom: ''
          }),
          
          // Initialize from localStorage
          initializeFromStorage: () => {
            const storedUsername = localStorage.getItem('username');
            if (storedUsername) {
              set({ 
                username: storedUsername, 
                isSignedIn: true 
              });
              const storedWalletAddress = localStorage.getItem('walletAddress') || null;
              socketService.setUsername(storedUsername, storedWalletAddress);
              console.log('🔄 Reconnection status:', storedUsername, storedWalletAddress);
            } else {
              const autoUsername = `Player${Math.floor(Math.random() * 1000)}`;
              set({ 
                username: autoUsername, 
                isSignedIn: false 
              });
              socketService.setUsername(autoUsername, null);
              console.log('🔄 Reconnection status:', autoUsername, null);
            }
          },
          
          // Removed: All session recovery and management functions
        }),
        {
          name: 'app-storage',
          partialize: (state) => ({
            username: state.username,
            isSignedIn: state.isSignedIn,
          }),
        }
      ),
      {
        name: 'app-store',
      }
    )
  )
);

export default useAppStore;